/**
 * Pure helpers for challenge prizes. Prizes are no longer paid automatically:
 * when a challenge ends the backend freezes the final standings and proposes
 * first place (`prizePending`), and an admin approves or rejects it
 * (`prizeDecision`). Kept free of React so Home and Challenges agree.
 */
import type { ChallengeSnapshotStanding, OutreachChallenge } from '@/api/types';

export type ChallengeState = 'needsApproval' | 'awarded' | 'rejected' | 'ended' | 'live' | 'paused' | 'upcoming';

/** Ended, standings frozen, and nobody has approved or rejected the prize yet. */
export function needsPrizeApproval(c: Pick<OutreachChallenge, 'prizePending' | 'prizeDecision'>): boolean {
  return !!c.prizePending && !c.prizeDecision;
}

/** How many challenges are waiting on a prize decision. */
export function pendingPrizeCount(challenges: readonly OutreachChallenge[] | undefined): number {
  return (challenges ?? []).filter(needsPrizeApproval).length;
}

export function challengeState(c: OutreachChallenge, now: Date): ChallengeState {
  if (needsPrizeApproval(c)) return 'needsApproval';
  if (c.prizeDecision?.decision === 'rejected') return 'rejected';
  if (c.prizeAwardedAt || c.prizeDecision?.decision === 'approved') return 'awarded';
  const started = new Date(c.startDate) <= now;
  const ended = new Date(c.endDate) < now;
  if (ended) return 'ended';
  if (!c.isActive) return 'paused';
  return started ? 'live' : 'upcoming';
}

/**
 * Frozen standings and decided prizes can't be edited: changing the metric or
 * dates would no longer match what was approved (or is about to be).
 */
export function isChallengeLocked(c: OutreachChallenge): boolean {
  return Boolean(c.prizeAwardedAt || c.prizePending || c.prizeDecision);
}

/** Everyone sharing first place in frozen standings (best first). */
export function tiedLeaders(standings: readonly ChallengeSnapshotStanding[]): ChallengeSnapshotStanding[] {
  const top = standings[0];
  if (!top) return [];
  return standings.filter((s) => s.value === top.value);
}

/** Pending challenges first; otherwise the backend's order. */
export function sortChallengesForReview<T extends OutreachChallenge>(challenges: readonly T[]): T[] {
  return [...challenges].sort((a, b) => Number(needsPrizeApproval(b)) - Number(needsPrizeApproval(a)));
}
